import React, { useState, useEffect } from 'react';
import { Camera, AlertCircle, Shield, RefreshCw, ExternalLink } from 'lucide-react';
import CameraService from '../utils/CameraService';

/**
 * PermissionHandler - Component for managing camera permission requests and error states
 * Provides user-friendly interface for permission handling with clear instructions
 */
const PermissionHandler = ({ 
  onPermissionGranted, 
  onPermissionDenied, 
  onError,
  className = '' 
}) => {
  const [permissionState, setPermissionState] = useState('checking'); // checking, granted, denied, unsupported, error
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    checkInitialPermission();
  }, []);

  const checkInitialPermission = async () => {
    try {
      const result = await CameraService.prototype.checkPermission();
      setPermissionState(result.state);
      
      if (result.error) {
        setErrorMessage(result.error);
      }
      
      // Auto-call callbacks based on initial state
      if (result.state === 'granted') {
        onPermissionGranted?.();
      } else if (result.state === 'denied') {
        onPermissionDenied?.(result.error);
      } else if (result.state === 'unsupported' || result.state === 'error') {
        onError?.(result.error);
      }
    } catch (error) {
      setPermissionState('error');
      setErrorMessage(error.message);
      onError?.(error.message);
    }
  };

  const requestPermission = async () => {
    setIsRequesting(true);
    setErrorMessage('');
    
    try {
      const cameraService = new CameraService();
      const result = await cameraService.requestPermission();
      
      if (result.success) {
        setPermissionState('granted');
        onPermissionGranted?.(result);
        
        // Clean up the test stream
        cameraService.cleanup();
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to access camera';
      setErrorMessage(errorMsg);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionState('denied');
        onPermissionDenied?.(errorMsg);
      } else {
        setPermissionState('error');
        onError?.(errorMsg);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const getBrowserInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome')) {
      return {
        browser: 'Chrome',
        steps: [
          'Click the camera icon in the address bar',
          'Select "Always allow" for camera access',
          'Click "Done" and refresh the page'
        ]
      };
    } else if (userAgent.includes('firefox')) {
      return {
        browser: 'Firefox',
        steps: [
          'Click the shield icon in the address bar',
          'Click "Turn off Blocking" for camera',
          'Refresh the page and try again'
        ]
      };
    } else if (userAgent.includes('safari')) {
      return {
        browser: 'Safari',
        steps: [
          'Go to Safari &gt; Preferences &gt; Websites',
          'Select "Camera" from the left sidebar',
          'Set this website to "Allow"'
        ]
      };
    } else if (userAgent.includes('edge')) {
      return {
        browser: 'Edge',
        steps: [
          'Click the camera icon in the address bar',
          'Select "Allow" for camera access',
          'Refresh the page'
        ]
      };
    }
    
    return {
      browser: 'your browser',
      steps: [
        'Look for a camera icon in your address bar',
        'Click it and allow camera access',
        'Refresh the page and try again'
      ]
    };
  };

  const renderCheckingState = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="animate-spin mb-4">
        <RefreshCw size={32} className="text-cyan-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Checking Camera Access
      </h3>
      <p className="text-sm text-gray-600">
        Please wait while we check your camera permissions...
      </p>
    </div>
  );

  const renderRequestState = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="bg-cyan-100 rounded-full p-4 mb-4">
        <Camera size={32} className="text-cyan-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Camera Access Required
      </h3>
      <p className="text-sm text-gray-600 mb-6 max-w-md">
        To take photos, we need access to your camera. Your privacy is important to us - 
        photos are only captured when you explicitly take them.
      </p>
      <button
        onClick={requestPermission}
        disabled={isRequesting}
        className="flex items-center space-x-2 bg-cyan-600 text-white px-6 py-3 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isRequesting ? (
          <>
            <RefreshCw size={16} className="animate-spin" />
            <span>Requesting Access...</span>
          </>
        ) : (
          <>
            <Camera size={16} />
            <span>Allow Camera Access</span>
          </>
        )}
      </button>
    </div>
  );

  const renderDeniedState = () => {
    const instructions = getBrowserInstructions();
    
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-red-100 rounded-full p-4 mb-4">
          <Shield size={32} className="text-red-600" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Camera Access Denied
        </h3>
        <p className="text-sm text-gray-600 mb-4 max-w-md">
          Camera access was denied. To take photos, please enable camera permissions for this site.
        </p>
        
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-w-md">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ExternalLink size={16} />
            <span>Show Instructions</span>
          </button>
          <button
            onClick={requestPermission}
            disabled={isRequesting}
            className="flex items-center space-x-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
          >
            {isRequesting ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span>Try Again</span>
          </button>
        </div>
        
        {showInstructions && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md text-left">
            <h4 className="font-medium text-blue-900 mb-3">
              Enable Camera in {instructions.browser}:
            </h4>
            <ol className="text-sm text-blue-800 space-y-2">
              {instructions.steps.map((step, index) => (
                <li key={index} className="flex items-start">
                  <span className="bg-blue-200 text-blue-900 rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium mr-3 mt-0.5 flex-shrink-0">
                    {index + 1}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: step }} />
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  const renderUnsupportedState = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="bg-gray-100 rounded-full p-4 mb-4">
        <AlertCircle size={32} className="text-gray-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Camera Not Supported
      </h3>
      <p className="text-sm text-gray-600 mb-4 max-w-md">
        Your browser doesn't support camera access. Please use a modern browser like Chrome, Firefox, Safari, or Edge.
      </p>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-w-md">
        <p className="text-sm text-yellow-800">
          <strong>Tip:</strong> You can still upload images from your device's gallery using the "Choose from Gallery" option.
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="bg-red-100 rounded-full p-4 mb-4">
        <AlertCircle size={32} className="text-red-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Camera Error
      </h3>
      <p className="text-sm text-gray-600 mb-4 max-w-md">
        There was an error accessing your camera. This might be due to hardware issues or another app using the camera.
      </p>
      
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-w-md">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={checkInitialPermission}
          disabled={isRequesting}
          className="flex items-center space-x-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
        >
          {isRequesting ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          <span>Try Again</span>
        </button>
      </div>
      
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-md">
        <p className="text-sm text-blue-800">
          <strong>Alternative:</strong> You can upload images from your device's gallery instead.
        </p>
      </div>
    </div>
  );

  const renderGrantedState = () => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="bg-green-100 rounded-full p-4 mb-4">
        <Camera size={32} className="text-green-600" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Camera Ready
      </h3>
      <p className="text-sm text-gray-600">
        Camera access granted. You can now take photos.
      </p>
    </div>
  );

  const renderContent = () => {
    switch (permissionState) {
      case 'checking':
        return renderCheckingState();
      case 'granted':
        return renderGrantedState();
      case 'denied':
        return renderDeniedState();
      case 'unsupported':
        return renderUnsupportedState();
      case 'error':
        return renderErrorState();
      default:
        return renderRequestState();
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {renderContent()}
    </div>
  );
};

export default PermissionHandler;